import {
  ChangeDetectorRef,
  Component,
  inject,
  input,
  OnInit,
  signal
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { LoadListener } from '@veone/ngx-utilities/rxjs';
import { delay, finalize, tap } from 'rxjs';
import { BaseComponent } from '~app/app/~shared/abstract-components/base.component';
import { MEDIA_TYPES } from '~app/app/~shared/constants/global.constants';
import { SharedModule } from '~app/app/~shared/modules/shared.module';
import { ChannelService } from '~app/app/~shared/services/channel.service';
import { NgSelectSearchService } from '~app/app/~shared/services/ng-select-search.service';
import { ScreenService } from '~app/app/~shared/services/screen.service';
import { ResourceFacade } from '~app/app/~store/facades/resource.facade';
import { Channel } from '~core/models/channel.models';
import { MediaType } from '~core/models/core.models';
import { Screen, ScreenData } from '~core/models/screen.models';
import { ScreenCreationDialogComponent } from '../../modals/screen-creation-dialog/screen-creation-dialog.component';

@Component({
  selector: 'screen-creation-form',
  templateUrl: './screen-creation-form.component.html',
  styleUrl: './screen-creation-form.component.scss',
  standalone: true,
  imports: [SharedModule]
})
export class ScreenCreationFormComponent
  extends BaseComponent
  implements OnInit
{
  private _screenService = inject(ScreenService);
  private _channelService = inject(ChannelService);
  private _resourceFacade = inject(ResourceFacade);
  private _dialogRef = inject(MatDialogRef<ScreenCreationDialogComponent>);
  private ngSelectSearchService = inject(NgSelectSearchService);
  private cdr = inject(ChangeDetectorRef);
  channelPagination = this.ngSelectSearchService.create<Channel>(this.cdr);
  private readonly pageSize = 10;

  channelListener: LoadListener<Channel[]> | undefined;

  screen = input<Screen | null>(null);
  mediaTypes = (({ poster, press, ...rest }) => rest)(MEDIA_TYPES);

  isSubmitting = signal(false);

  form = new FormGroup({
    name: new FormControl<string>('', {
      validators: [Validators.required, Validators.minLength(3)]
    }),
    mediaType: new FormControl<string>('', {
      validators: [Validators.required, Validators.minLength(3)]
    }),
    channelId: new FormControl<string | null>(null, {
      validators: [Validators.required, Validators.minLength(3)]
    })
  });

  violations$ = this._globalFacade.formViolations$.pipe(
    delay(500),
    tap({
      next: violations => {
        violations?.forEach(violation =>
          this.form.get(violation.propertyPath)?.setErrors({
            httpError: violation.title
          })
        );
      }
    })
  );

  ngOnInit(): void {
    //this.initChannelListener(this.screen()?.mediaType);
    this.patchFormValues(this.screen());
    this.channelWireSearch();
  }

  onSubmit(): void {
    this.isSubmitting.set(true);
    const screenId = this.screen()?.id;
    if (screenId) {
      this.updateScreen(screenId);
    } else {
      this.createScreen();
    }
  }

  private resolveMediaType(): MediaType {
    const v = this.form.controls.mediaType.value;
    return (
      (v && v.trim() ? (v as MediaType) : this.screen()?.mediaType) ?? 'poster'
    );
  }

  createScreen(): void {
    const formValue = this.form.value as ScreenData;

    this._screenService
      .create(formValue)
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        tap({
          next: () => {
            this._dialogRef.close(true);
            this._snackbarService.open({
              text: `Ecran créé avec succès`,
              type: 'success'
            });

            this._resourceFacade.getData({ refresh: true });
          }
        }),
        finalize(() => this.isSubmitting.set(false))
      )
      .subscribe();
  }

  updateScreen(screenId: string): void {
    const formValue = this.form.value as ScreenData;

    this._screenService
      .update(screenId, formValue)
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        tap({
          next: () => {
            this._dialogRef.close(true);
            this._snackbarService.open({
              text: `Ecran modifié avec succès`,
              type: 'success'
            });

            this._resourceFacade.getData({ refresh: true });
          }
        }),
        finalize(() => this.isSubmitting.set(false))
      )
      .subscribe();
  }

  patchFormValues(screen: Screen | null): void {
    if (screen) {
      this.form.patchValue({
        name: screen.name,
        mediaType: screen.mediaType,
        channelId: screen.channel.id
      });
      this._cdRef.detectChanges();
    }
  }

  channelWireSearch(): void {
    this.channelPagination
      .initSearch(params => this._channelService.list(params), {
        itemPerPage: this.pageSize,
        mediaType: this.resolveMediaType()
      })
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe();

    this.channelPagination.search('');
  }

  onChannelScroll(): void {
    this.channelPagination
      .loadMore(params =>
        this._channelService.list({
          ...params,
          mediaType: this.resolveMediaType(),
          itemPerPage: this.pageSize
        })
      )
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe();
  }

  onMediaTypeChange(): void {
    this.channelPagination.clear();
    this.channelWireSearch();
  }
}
